/**
 * Mission state: drafts being planned, missions actually on aircraft, and zones.
 *
 * This is a store rather than a hook because of a specific failure. In the
 * predecessor the mission list lived in a `useState` inside a hook called from
 * the planning screen only, so the command screen could not read a mission at
 * all. What it got instead was a `Set` of drone ids that were known to have
 * one, which is why the flight display could tell you an aircraft had a route
 * but never show it, and why "resume mission" had to ask the vehicle first.
 *
 * The other thing this file is careful about is the difference between a draft
 * and what is loaded on the airframe:
 *
 *   `draft`    the active plan's routes, including unsaved local edits.
 *   `uploaded` what the server recorded after a vehicle acknowledged a transfer.
 *
 * Display prefers `uploaded`, always. A draft is what somebody intends; an
 * uploaded mission is what the aircraft will fly if it is switched to AUTO, and
 * showing the intention over the fact is how an operator ends up believing a
 * revision reached the vehicle when the upload was rejected.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { uploadMission as uploadToVehicle, downloadMission } from '@/api/drones';
import * as missionsApi from '@/api/missions';
import { enqueue, registerSender } from '@/stores/outbox';
import { MavCmd, MavFrame, type DroneId, type MissionPlan, type Waypoint } from '@/types/domain';

/**
 * An operating area drawn on the map.
 *
 * Shared across the deployment through /api/zones. The server refuses a route,
 * a goto or a formation that crosses one. An aircraft enforces them itself only
 * once somebody who commands it pushes them as its fence (api/fence.ts); saving
 * a zone here writes to no aircraft.
 */
export interface ZoneCircle {
  type: 'circle';
  lat: number;
  lon: number;
  radiusM: number;
}

export interface ZonePolygon {
  type: 'polygon';
  points: Array<{ lat: number; lon: number }>;
}

export type ZoneShape = ZoneCircle | ZonePolygon;

export interface Zone {
  zoneId: string;
  name: string;
  /** keep-in is the area to stay inside; keep-out is the area to stay clear of. */
  kind: 'keep-in' | 'keep-out';
  shape: ZoneShape;
}

/** Default waypoint altitude, metres above the launch point. */
export const DEFAULT_WAYPOINT_ALT_M = 40;

export function makeWaypoint(lat: number, lon: number, alt: number): Waypoint {
  return {
    lat,
    lon,
    alt,
    command: MavCmd.NavWaypoint,
    // Relative to the launch altitude, which is what an operator means by "40
    // metres". Absolute AMSL in the same field puts the aircraft underground at
    // any site above sea level.
    frame: MavFrame.GlobalRelativeAlt,
    param1: 0,
    param2: 0,
    param3: 0,
    param4: 0,
    autocontinue: true,
  };
}

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance in metres. Mirrors api/app/domain/geo.haversine_m. */
export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const phi1 = lat1 * toRad;
  const phi2 = lat2 * toRad;
  const dPhi = (lat2 - lat1) * toRad;
  const dLambda = (lon2 - lon1) * toRad;
  const a =
    Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/** Total ground track of a route, ignoring altitude. */
export function groundTrackM(waypoints: Waypoint[]): number {
  let total = 0;
  for (let i = 1; i < waypoints.length; i += 1) {
    const previous = waypoints[i - 1];
    const current = waypoints[i];
    if (!previous || !current) continue;
    total += haversineM(previous.lat, previous.lon, current.lat, current.lon);
  }
  return total;
}

/** Waypoints that describe a position. RTL and DO_ commands carry no coordinate. */
export function isPositional(waypoint: Waypoint): boolean {
  return (
    waypoint.command !== MavCmd.NavReturnToLaunch &&
    waypoint.command !== MavCmd.DoChangeSpeed &&
    !(waypoint.lat === 0 && waypoint.lon === 0)
  );
}

export type MissionMap = Record<DroneId, Waypoint[]>;

/** One shared empty route, so "no waypoints" has a stable identity. */
const NO_WAYPOINTS: Waypoint[] = [];

interface MissionStoreState {
  plans: MissionPlan[];
  activePlanId: string | null;
  /** The active plan's routes plus local edits. Replaced whole on every change. */
  draft: MissionMap;
  /** What the server says is on each aircraft. */
  uploaded: MissionMap;
  /** Drones whose draft differs from what was last saved to the plan. */
  dirty: DroneId[];
  zones: Zone[];
  /** The etag the zone list was last read or written at. */
  zonesEtag: string | null;
  /** Which drone the next map click adds a waypoint to. */
  editingDroneId: DroneId | null;
  defaultAltM: number;
  busy: boolean;
  error: string | null;

  loadPlans: () => Promise<void>;
  loadUploaded: () => Promise<void>;
  selectPlan: (planId: string) => Promise<void>;
  createPlan: (name: string) => Promise<void>;
  renamePlan: (planId: string, name: string) => Promise<void>;
  setCollaborators: (planId: string, collaboratorIds: string[]) => Promise<void>;
  removePlan: (planId: string) => Promise<void>;
  joinPlan: (planId: string) => Promise<void>;
  leavePlan: (planId: string) => Promise<void>;

  setEditingDrone: (droneId: DroneId | null) => void;
  setDefaultAlt: (altM: number) => void;

  appendWaypoint: (droneId: DroneId, lat: number, lon: number, alt?: number) => void;
  moveWaypoint: (droneId: DroneId, index: number, lat: number, lon: number) => void;
  updateWaypoint: (droneId: DroneId, index: number, changes: Partial<Waypoint>) => void;
  removeWaypoint: (droneId: DroneId, index: number) => void;
  clearDraft: (droneId: DroneId) => void;
  /** Copy what is on the aircraft into the draft, to revise it rather than restart. */
  adoptUploaded: (droneId: DroneId) => void;
  setDraft: (droneId: DroneId, waypoints: Waypoint[]) => void;

  savePlan: () => Promise<boolean>;
  uploadToDrone: (droneId: DroneId) => Promise<{ ok: boolean; message: string }>;
  applyToDrones: (
    sourceDroneId: DroneId,
    targetIds: DroneId[],
  ) => Promise<Array<{ droneId: DroneId; ok: boolean; message: string }>>;
  downloadFromDrone: (droneId: DroneId) => Promise<{ ok: boolean; message: string }>;

  /** Pull the shared zone list from the server. */
  loadZones: () => Promise<void>;
  addZone: (zone: Omit<Zone, 'zoneId'>) => string;
  /** Change a circular zone's radius, in metres. Polygons are unaffected. */
  resizeZone: (zoneId: string, radiusM: number) => void;
  removeZone: (zoneId: string) => void;
  clearZones: () => void;
  /** Show a zone's new shape mid-drag. Local only: nothing is written. */
  previewZone: (zone: Zone) => void;
  /** Save the zones once a drag ends, refused if anyone saved since they were read. */
  commitZones: () => Promise<ZoneSaveResult>;

  reset: () => void;
}

/**
 * A zone id no other client, and no earlier page load of this one, can mint.
 *
 * It was a counter from 1 per page load, so the first zone drawn after a
 * reload was "zone-1" again, collided with the "zone-1" already on the server,
 * and the save failed on the unique key while the map went on showing a zone
 * that nobody else could see.
 */
function newZoneId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `zone-${Date.now().toString(36)}-${random}`;
}

function withDirty(current: DroneId[], droneId: DroneId): DroneId[] {
  return current.includes(droneId) ? current : [...current, droneId];
}

function editRoute(
  state: MissionStoreState,
  droneId: DroneId,
  edit: (waypoints: Waypoint[]) => Waypoint[],
): Pick<MissionStoreState, 'draft' | 'dirty'> {
  const next = edit(state.draft[droneId] ?? []);
  return {
    draft: { ...state.draft, [droneId]: next },
    dirty: withDirty(state.dirty, droneId),
  };
}

/**
 * Write the whole zone list back after any change.
 *
 * Zones are deployment-wide, so a local edit that is not pushed is a boundary
 * only one operator can see — the exact failure server-side zones exist to fix.
 * Fire-and-forget: the edit is already applied locally and a failed write is
 * surfaced by the next read rather than by rolling the map back under the
 * operator's cursor.
 */
function pushZones(): void {
  void saveZonesInOrder(false);
}

export interface ZoneSaveResult {
  ok: boolean;
  /** Somebody else saved first; the list has been reloaded from the server. */
  conflict: boolean;
  message: string;
}

/**
 * One save at a time, each sending the list as it stands when its turn comes.
 *
 * Out-of-order writes are what made the predecessor's zones jitter: it saved on
 * every mousemove, the writes landed in a different order than they were sent,
 * and the one the server kept was often a mid-drag shape. Queuing also means a
 * conditional save is checked against the etag the previous save produced,
 * not one it raced.
 */
let zoneSaves: Promise<unknown> = Promise.resolve();

function saveZonesInOrder(conditional: boolean): Promise<ZoneSaveResult> {
  const run = async (): Promise<ZoneSaveResult> => {
    const { zones, zonesEtag } = useMissionStore.getState();
    const result = await missionsApi.saveZones(
      zones,
      conditional && zonesEtag ? zonesEtag : undefined,
    );
    if (result.ok) {
      useMissionStore.setState({ zonesEtag: result.data.etag ?? null });
      return { ok: true, conflict: false, message: 'Zone saved' };
    }
    if (result.error.kind === 'conflict') {
      await useMissionStore.getState().loadZones();
      return {
        ok: false,
        conflict: true,
        message:
          'Somebody else changed the zones while you were editing. Your change was not saved, and the map now shows the current zones.',
      };
    }
    return { ok: false, conflict: false, message: `Zone not saved: ${result.error.message}` };
  };
  const next = zoneSaves.then(run, run);
  zoneSaves = next.catch(() => undefined);
  return next;
}

export const useMissionStore = create<MissionStoreState>((set, get) => ({
  plans: [],
  activePlanId: null,
  draft: {},
  uploaded: {},
  dirty: [],
  zones: [],
  zonesEtag: null,
  editingDroneId: null,
  defaultAltM: DEFAULT_WAYPOINT_ALT_M,
  busy: false,
  error: null,

  loadPlans: async () => {
    set({ busy: true, error: null });
    const [list, active] = await Promise.all([missionsApi.listPlans(), missionsApi.getActivePlan()]);
    if (!list.ok) {
      set({ busy: false, error: list.error.message });
      return;
    }
    if (!active.ok) {
      set({ busy: false, plans: list.data.plans, error: active.error.message });
      return;
    }
    set({
      busy: false,
      plans: list.data.plans,
      activePlanId: active.data.planId,
      draft: active.data.missions,
      dirty: [],
      error: null,
    });
  },

  loadUploaded: async () => {
    const result = await missionsApi.listUploadedMissions();
    if (!result.ok) return;
    // Command re-reads this on a timer now that the server reads each
    // aircraft's mission itself. An unchanged answer keeps the old identity,
    // so the map's routes are not rebuilt every few seconds for nothing.
    if (JSON.stringify(result.data.missions) === JSON.stringify(get().uploaded)) return;
    set({ uploaded: result.data.missions });
  },

  selectPlan: async (planId) => {
    set({ busy: true, error: null });
    const result = await missionsApi.setActivePlan(planId);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return;
    }
    const plan = await missionsApi.getPlan(planId);
    if (!plan.ok) {
      set({ busy: false, error: plan.error.message });
      return;
    }
    // Unsaved edits belong to the plan being left, not the one being opened.
    // Carrying them across is how a route appeared in a colleague's plan.
    set({ busy: false, activePlanId: planId, draft: plan.data.missions, dirty: [], error: null });
  },

  createPlan: async (name) => {
    set({ busy: true, error: null });
    const result = await missionsApi.createPlan(name);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return;
    }
    set((state) => ({
      busy: false,
      plans: [result.data, ...state.plans],
      activePlanId: result.data.planId,
      draft: result.data.missions,
      dirty: [],
    }));
  },

  renamePlan: async (planId, name) => {
    const result = await missionsApi.updatePlanMeta(planId, { name });
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    set((state) => ({
      plans: state.plans.map((plan) => (plan.planId === planId ? result.data : plan)),
    }));
  },

  setCollaborators: async (planId, collaboratorIds) => {
    const result = await missionsApi.updatePlanMeta(planId, { collaboratorIds });
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    set((state) => ({
      plans: state.plans.map((plan) => (plan.planId === planId ? result.data : plan)),
    }));
  },

  removePlan: async (planId) => {
    const result = await missionsApi.deletePlan(planId);
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    const remaining = get().plans.filter((plan) => plan.planId !== planId);
    set({ plans: remaining });
    if (get().activePlanId === planId) {
      set({ activePlanId: null, draft: {}, dirty: [] });
      await get().loadPlans();
    }
  },

  joinPlan: async (planId) => {
    const result = await missionsApi.joinPlan(planId);
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    set((state) => ({
      plans: state.plans.map((plan) => (plan.planId === planId ? result.data : plan)),
    }));
  },

  leavePlan: async (planId) => {
    const result = await missionsApi.leavePlan(planId);
    if (!result.ok) {
      set({ error: result.error.message });
      return;
    }
    set((state) => ({ plans: state.plans.filter((plan) => plan.planId !== planId) }));
    if (get().activePlanId === planId) await get().loadPlans();
  },

  setEditingDrone: (droneId) => set({ editingDroneId: droneId }),

  setDefaultAlt: (altM) => set({ defaultAltM: altM }),

  appendWaypoint: (droneId, lat, lon, alt) =>
    set((state) =>
      editRoute(state, droneId, (waypoints) => [
        ...waypoints,
        makeWaypoint(lat, lon, alt ?? state.defaultAltM),
      ]),
    ),

  moveWaypoint: (droneId, index, lat, lon) =>
    set((state) =>
      editRoute(state, droneId, (waypoints) =>
        waypoints.map((waypoint, i) => (i === index ? { ...waypoint, lat, lon } : waypoint)),
      ),
    ),

  updateWaypoint: (droneId, index, changes) =>
    set((state) =>
      editRoute(state, droneId, (waypoints) =>
        waypoints.map((waypoint, i) => (i === index ? { ...waypoint, ...changes } : waypoint)),
      ),
    ),

  removeWaypoint: (droneId, index) =>
    set((state) =>
      editRoute(state, droneId, (waypoints) => waypoints.filter((_, i) => i !== index)),
    ),

  clearDraft: (droneId) => set((state) => editRoute(state, droneId, () => [])),

  adoptUploaded: (droneId) =>
    set((state) => {
      const onVehicle = state.uploaded[droneId];
      if (!onVehicle) return {};
      return editRoute(state, droneId, () => onVehicle.map((waypoint) => ({ ...waypoint })));
    }),

  setDraft: (droneId, waypoints) => set((state) => editRoute(state, droneId, () => waypoints)),

  savePlan: async () => {
    const { activePlanId, draft } = get();
    if (!activePlanId) {
      set({ error: 'No plan is open, so there is nothing to save.' });
      return false;
    }
    set({ busy: true, error: null });
    const result = await missionsApi.savePlanMissions(activePlanId, draft);
    if (!result.ok) {
      // A route planned in a dead zone is still the route an hour later, so a
      // lost link queues it rather than throwing the work away. Anything the
      // server actually refused is a real error and surfaces as one.
      if (result.error.kind === 'network' || result.error.kind === 'timeout') {
        const plan = get().plans.find((p) => p.planId === activePlanId);
        enqueue(
          'plan-save',
          { planId: activePlanId, draft },
          `Route for ${plan?.name ?? activePlanId}`,
          activePlanId,
        );
        set({
          busy: false,
          dirty: [],
          error: null,
        });
        return true;
      }
      set({ busy: false, error: result.error.message });
      return false;
    }
    set((state) => ({
      busy: false,
      dirty: [],
      plans: state.plans.map((plan) => (plan.planId === activePlanId ? result.data : plan)),
    }));
    return true;
  },

  /**
   * Send a route to an aircraft.
   *
   * The waypoints on screen are sent explicitly rather than by plan id. The
   * plan on the server can be behind the draft in front of the operator, and an
   * upload that transfers something other than what is displayed is precisely
   * the failure this whole split exists to prevent.
   *
   * `uploaded` is written from the server's response, which is empty unless the
   * vehicle acknowledged, so a rejected transfer cannot leave the map showing a
   * route the aircraft never received.
   */
  uploadToDrone: async (droneId) => {
    const waypoints = get().draft[droneId] ?? [];
    if (!waypoints.length) {
      return { ok: false, message: 'This drone has no waypoints to upload.' };
    }
    set({ busy: true, error: null });
    const result = await uploadToVehicle(droneId, waypoints);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    set((state) => ({
      busy: false,
      uploaded: { ...state.uploaded, [droneId]: result.data.waypoints },
    }));
    return { ok: true, message: result.data.result.message };
  },

  /**
   * Apply one aircraft's draft to a set of aircraft and upload it to each.
   *
   * The source's route is copied into every target's draft first, so the map
   * and panel show what each aircraft was sent even if a later upload in the
   * batch is refused. Uploads run one at a time — a burst of parallel mission
   * transfers is how a shared radio link gets saturated — and every aircraft's
   * outcome is returned rather than collapsed into one pass/fail.
   */
  applyToDrones: async (sourceDroneId, targetIds) => {
    const source = get().draft[sourceDroneId] ?? [];
    if (!source.length) {
      return targetIds.map((droneId) => ({
        droneId,
        ok: false,
        message: 'No draft to apply.',
      }));
    }
    const outcomes: Array<{ droneId: DroneId; ok: boolean; message: string }> = [];
    for (const droneId of targetIds) {
      if (droneId !== sourceDroneId) {
        get().setDraft(droneId, source.map((waypoint) => ({ ...waypoint })));
      }
      const result = await get().uploadToDrone(droneId);
      outcomes.push({ droneId, ok: result.ok, message: result.message });
    }
    return outcomes;
  },

  downloadFromDrone: async (droneId) => {
    set({ busy: true, error: null });
    const result = await downloadMission(droneId);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    set((state) => ({
      busy: false,
      uploaded: { ...state.uploaded, [droneId]: result.data.waypoints },
    }));
    return {
      ok: true,
      message: `${result.data.waypoints.length} waypoints read back from the aircraft`,
    };
  },

  loadZones: async () => {
    const result = await missionsApi.listZones();
    if (result.ok) set({ zones: result.data.zones, zonesEtag: result.data.etag ?? null });
  },

  addZone: (zone) => {
    const zoneId = newZoneId();
    set((state) => ({ zones: [...state.zones, { ...zone, zoneId }] }));
    pushZones();
    return zoneId;
  },

  resizeZone: (zoneId, radiusM) => {
    set((state) => ({
      zones: state.zones.map((zone) =>
        zone.zoneId === zoneId && zone.shape.type === 'circle'
          ? { ...zone, shape: { ...zone.shape, radiusM: Math.max(1, radiusM) } }
          : zone,
      ),
    }));
    pushZones();
  },

  removeZone: (zoneId) => {
    set((state) => ({ zones: state.zones.filter((zone) => zone.zoneId !== zoneId) }));
    pushZones();
  },

  clearZones: () => {
    set({ zones: [] });
    pushZones();
  },

  previewZone: (zone) => {
    set((state) => ({
      zones: state.zones.map((current) => (current.zoneId === zone.zoneId ? zone : current)),
    }));
  },

  commitZones: () => saveZonesInOrder(true),

  reset: () =>
    set({
      plans: [],
      activePlanId: null,
      draft: {},
      uploaded: {},
      dirty: [],
      zones: [],
      zonesEtag: null,
      editingDroneId: null,
      busy: false,
      error: null,
    }),
}));

/**
 * Watch mission geometry without React, the way the map layers do.
 *
 * Fires when a draft route, an uploaded mission or a zone changes identity.
 * Everything else in the store (plan names, busy flags, error strings) is
 * chrome and must not redraw the map.
 */
export function subscribeToMissionGeometry(
  callback: (geometry: { draft: MissionMap; uploaded: MissionMap; zones: Zone[] }) => void,
): () => void {
  let previous = useMissionStore.getState();
  callback({ draft: previous.draft, uploaded: previous.uploaded, zones: previous.zones });

  return useMissionStore.subscribe((state) => {
    if (
      state.draft === previous.draft &&
      state.uploaded === previous.uploaded &&
      state.zones === previous.zones
    ) {
      return;
    }
    previous = state;
    callback({ draft: state.draft, uploaded: state.uploaded, zones: state.zones });
  });
}

/**
 * Actions, at module scope and stable forever.
 *
 * Components call these instead of subscribing to the store for them. A
 * component that does `const store = useMissionStore()` to reach one action has
 * subscribed to every field in this file, which is how the predecessor's
 * planning screen came to re-render on unrelated telemetry.
 */
export const missionActions = {
  loadPlans: () => useMissionStore.getState().loadPlans(),
  loadUploaded: () => useMissionStore.getState().loadUploaded(),
  selectPlan: (planId: string) => useMissionStore.getState().selectPlan(planId),
  createPlan: (name: string) => useMissionStore.getState().createPlan(name),
  renamePlan: (planId: string, name: string) =>
    useMissionStore.getState().renamePlan(planId, name),
  removePlan: (planId: string) => useMissionStore.getState().removePlan(planId),
  joinPlan: (planId: string) => useMissionStore.getState().joinPlan(planId),
  leavePlan: (planId: string) => useMissionStore.getState().leavePlan(planId),
  savePlan: () => useMissionStore.getState().savePlan(),
  uploadToDrone: (droneId: DroneId) => useMissionStore.getState().uploadToDrone(droneId),
  applyToDrones: (sourceDroneId: DroneId, targetIds: DroneId[]) =>
    useMissionStore.getState().applyToDrones(sourceDroneId, targetIds),
  downloadFromDrone: (droneId: DroneId) => useMissionStore.getState().downloadFromDrone(droneId),
  appendWaypoint: (droneId: DroneId, lat: number, lon: number, alt?: number) =>
    useMissionStore.getState().appendWaypoint(droneId, lat, lon, alt),
  moveWaypoint: (droneId: DroneId, index: number, lat: number, lon: number) =>
    useMissionStore.getState().moveWaypoint(droneId, index, lat, lon),
  updateWaypoint: (droneId: DroneId, index: number, changes: Partial<Waypoint>) =>
    useMissionStore.getState().updateWaypoint(droneId, index, changes),
  removeWaypoint: (droneId: DroneId, index: number) =>
    useMissionStore.getState().removeWaypoint(droneId, index),
  clearDraft: (droneId: DroneId) => useMissionStore.getState().clearDraft(droneId),
  adoptUploaded: (droneId: DroneId) => useMissionStore.getState().adoptUploaded(droneId),
  setDraft: (droneId: DroneId, waypoints: Waypoint[]) =>
    useMissionStore.getState().setDraft(droneId, waypoints),
  setEditingDrone: (droneId: DroneId | null) =>
    useMissionStore.getState().setEditingDrone(droneId),
  setDefaultAlt: (altM: number) => useMissionStore.getState().setDefaultAlt(altM),
  loadZones: () => useMissionStore.getState().loadZones(),
  addZone: (zone: Omit<Zone, 'zoneId'>) => useMissionStore.getState().addZone(zone),
  resizeZone: (zoneId: string, radiusM: number) =>
    useMissionStore.getState().resizeZone(zoneId, radiusM),
  removeZone: (zoneId: string) => useMissionStore.getState().removeZone(zoneId),
  clearZones: () => useMissionStore.getState().clearZones(),
  previewZone: (zone: Zone) => useMissionStore.getState().previewZone(zone),
  commitZones: () => useMissionStore.getState().commitZones(),
  getDraft: (droneId: DroneId): Waypoint[] => useMissionStore.getState().draft[droneId] ?? [],
  getUploaded: (droneId: DroneId): Waypoint[] => useMissionStore.getState().uploaded[droneId] ?? [],
  editingDroneId: (): DroneId | null => useMissionStore.getState().editingDroneId,
  reset: () => useMissionStore.getState().reset(),
};

/* Selectors. Each one answers a single question so a waypoint edit on one drone
 * does not re-render the panel for another. */

export function useMissionPlans(): MissionPlan[] {
  return useMissionStore((state) => state.plans);
}

export function useActivePlan(): MissionPlan | null {
  return useMissionStore(
    (state) => state.plans.find((plan) => plan.planId === state.activePlanId) ?? null,
  );
}

export function useActivePlanId(): string | null {
  return useMissionStore((state) => state.activePlanId);
}

export function useDraftMission(droneId: DroneId | null): Waypoint[] | undefined {
  return useMissionStore((state) => (droneId === null ? undefined : state.draft[droneId]));
}

export function useUploadedMission(droneId: DroneId | null): Waypoint[] | undefined {
  return useMissionStore((state) => (droneId === null ? undefined : state.uploaded[droneId]));
}

/**
 * What to show for a drone: the mission on the aircraft when there is one,
 * otherwise the draft. `onVehicle` says which, because the answer changes what
 * an operator is allowed to assume.
 */
export function useDisplayMission(droneId: DroneId | null): {
  waypoints: Waypoint[];
  onVehicle: boolean;
} {
  return useMissionStore(
    useShallow((state) => {
      if (droneId === null) return { waypoints: NO_WAYPOINTS, onVehicle: false };
      const onVehicle = state.uploaded[droneId];
      if (onVehicle && onVehicle.length) return { waypoints: onVehicle, onVehicle: true };
      // The shared constant matters: a fresh `[]` here is a new identity on
      // every read, the shallow comparison never settles, and React tears the
      // component down with "getSnapshot should be cached".
      return { waypoints: state.draft[droneId] ?? NO_WAYPOINTS, onVehicle: false };
    }),
  );
}

export function useDroneIdsWithMissions(): DroneId[] {
  return useMissionStore(
    useShallow((state) => {
      const ids = new Set<DroneId>();
      for (const [id, waypoints] of Object.entries(state.uploaded)) {
        if (waypoints.length) ids.add(id);
      }
      for (const [id, waypoints] of Object.entries(state.draft)) {
        if (waypoints.length) ids.add(id);
      }
      return [...ids].sort();
    }),
  );
}

export function useZones(): Zone[] {
  return useMissionStore((state) => state.zones);
}

export function useMissionBusy(): boolean {
  return useMissionStore((state) => state.busy);
}

export function useMissionError(): string | null {
  return useMissionStore((state) => state.error);
}

export function useDirtyDrones(): DroneId[] {
  return useMissionStore((state) => state.dirty);
}

export function useEditingDroneId(): DroneId | null {
  return useMissionStore((state) => state.editingDroneId);
}

export function useDefaultAlt(): number {
  return useMissionStore((state) => state.defaultAltM);
}

// How a queued route save is replayed once the link is back. Registered here
// because this module owns what a plan save means; the outbox only owns when.
registerSender('plan-save', (entry) =>
  missionsApi.savePlanMissions(
    entry.payload.planId as string,
    entry.payload.draft as MissionMap,
  ),
);
